// RIC-5.6C — shared Search/Map filter URL + backend parameter contract.
//
// Pure functions only — no React, no network, no screen orchestration.
// Screen-specific behavior (Search sort/pagination, Map page_size) stays
// in the consuming ScreenContent widgets.

export const DEFAULT_SORT = "newest";

/** Stable URL sort tokens → DRF OrderingFilter values for /search and /map. */
export const SORT_TO_ORDERING = {
  newest: "-created_at",
  oldest: "created_at",
  updated: "-updated_at",
  popular: "-likes_count_total,-created_at",
  price_asc: "price",
  price_desc: "-price",
};

export function normalizeSearchSort(sort) {
  return sort && SORT_TO_ORDERING[sort] ? sort : DEFAULT_SORT;
}

export function orderingFromSearchSort(sort) {
  return SORT_TO_ORDERING[normalizeSearchSort(sort)];
}

export const KATOTTG_CODE_RE = /^UA\d{17}$/;

export const PROPERTY_COMPENSATION_FILTERS = [
  { value: "", label: "Усі" },
  { value: "configured", label: "Є" },
  { value: "none", label: "Немає" },
  { value: "percent", label: "Відсоток" },
  { value: "fixed", label: "Фіксована" },
];

export const PROPERTY_COMPENSATION_CURRENCY_FILTERS = [
  { value: "", label: "Усі валюти" },
  { value: "UAH", label: "UAH" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

const ALLOWED_PROPERTY_COMPENSATIONS = new Set(
  PROPERTY_COMPENSATION_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_PROPERTY_COMPENSATION_CURRENCIES = new Set(
  PROPERTY_COMPENSATION_CURRENCY_FILTERS.map((item) => item.value).filter(
    Boolean,
  ),
);

/**
 * Validates compensation filter pair. Returns { valid, errors } where errors
 * may contain `compensation` and/or `compensation_currency` string messages.
 *
 * Invalid conditions:
 *   - non-empty compensation not in the configured set
 *   - non-empty currency not in the configured set
 *   - none + non-empty currency (incompatible combination)
 */
export function validatePropertyCompensationFilters(filters) {
  const compensation = (filters.compensation || "").trim();
  const currency = (filters.compensation_currency || "").trim();
  const errors = {};

  if (compensation !== "" && !ALLOWED_PROPERTY_COMPENSATIONS.has(compensation)) {
    errors.compensation = `Некоректний тип винагороди: ${compensation}`;
  }
  if (currency !== "" && !ALLOWED_PROPERTY_COMPENSATION_CURRENCIES.has(currency)) {
    errors.compensation_currency = `Некоректна валюта: ${currency}`;
  }
  if (
    compensation === "none"
    && currency !== ""
    && !errors.compensation_currency
  ) {
    errors.compensation_currency = `Для «Немає» валюта не застосовується`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

/** Combine compensation validation error messages into a single string. */
export function propertyCompensationFilterErrorMessage(errors) {
  return [errors.compensation, errors.compensation_currency]
    .filter(Boolean)
    .join("; ");
}

/**
 * @deprecated Thin trim-only helper — no longer strips invalid values.
 * Kept for export compatibility. Callers on the read path have been removed.
 */
export function normalizePropertyCompensationFilters(filters) {
  const compensation = (filters.compensation || "").trim();
  const compensation_currency = (filters.compensation_currency || "").trim();
  if (
    compensation === filters.compensation
    && compensation_currency === (filters.compensation_currency || "")
  ) {
    return filters;
  }
  return { ...filters, compensation, compensation_currency };
}

export const EMPTY_FILTER = {
  q: "",
  deal_type: "",
  property_type: "",
  region_code: "",
  settlement_code: "",
  address_object: "",
  local_area: "",
  /** UI-only street label; never serialized to URL; never treated as canonical. */
  address_object_name: "",
  /** True only after autocomplete select or successful canonical AddressObject lookup. */
  address_object_name_verified: false,
  /** Internal — never serialized to URL; true only after canonical unit lookup. */
  settlement_code_verified: false,
  /** Internal — never serialized to URL. */
  settlement_verification_status: "idle",
  /** Internal — never serialized to URL. */
  settlement_verification_error: "",
  price_min: "",
  price_max: "",
  currency: "USD",
  area_min: "",
  area_max: "",
  rooms_min: "",
  rooms_max: "",
  floor_min: "",
  floor_max: "",
  land_purpose: "",
  suitable_for: "",
  residential_complex: "",
  cadastral_number: "",
  walls: "",
  heating: "",
  condition: "",
  furniture: "",
  features: [],
  has_housing_program: false,
  has_recovery_program: false,
  compensation: "",
  compensation_currency: "",
};

export const SETTLEMENT_VERIFICATION_STATUS = {
  idle: "idle",
  pending: "pending",
  verified: "verified",
  rejected: "rejected",
  error: "error",
};

function withSettlementVerification(filters, { verified, status, error = "" }) {
  return {
    ...filters,
    settlement_code_verified: Boolean(verified),
    settlement_verification_status: status,
    settlement_verification_error: error || "",
  };
}

export function sanitizeSettlementCodeInFilters(filters) {
  const code = (filters.settlement_code || "").trim();
  if (!code) {
    if (!filters.settlement_code) return filters;
    return withSettlementVerification(
      { ...filters, settlement_code: "" },
      { verified: false, status: SETTLEMENT_VERIFICATION_STATUS.idle },
    );
  }
  if (!KATOTTG_CODE_RE.test(code)) {
    return withSettlementVerification(
      { ...filters, settlement_code: "" },
      { verified: false, status: SETTLEMENT_VERIFICATION_STATUS.idle },
    );
  }
  return { ...filters, settlement_code: code };
}

export function normalizeGeographyFilters(filters) {
  const sanitizedInput = { ...filters };
  delete sanitizedInput.region;
  delete sanitizedInput.city;
  delete sanitizedInput.district;
  delete sanitizedInput.address;
  delete sanitizedInput.active_only;
  let sanitized = sanitizeRegionCodeInFilters(sanitizedInput);
  sanitized = sanitizeSettlementCodeInFilters(sanitized);
  const regionCode = (sanitized.region_code || "").trim();
  if (!regionCode) {
    return withSettlementVerification(
      {
        ...sanitized,
        settlement_code: "",
        address_object: "",
        address_object_name: "",
        address_object_name_verified: false,
      },
      { verified: false, status: SETTLEMENT_VERIFICATION_STATUS.idle },
    );
  }
  return {
    ...sanitized,
  };
}

/**
 * Merge URL-parsed settlement candidate with draft verification state.
 * Apply / back-forward must not overwrite a matching verified (or rejected)
 * draft with the unverified URL snapshot. An empty draft against a URL
 * settlement is a pending candidate, not a silent clear.
 */
export function mergeSettlementVerification(urlFilters, draftFilters) {
  const urlCode = (urlFilters.settlement_code || "").trim();
  if (!urlCode) {
    return withSettlementVerification(
      { ...urlFilters },
      { verified: false, status: SETTLEMENT_VERIFICATION_STATUS.idle },
    );
  }
  const draftCode = (draftFilters.settlement_code || "").trim();
  if (urlCode !== draftCode) {
    return withSettlementVerification(
      { ...urlFilters },
      { verified: false, status: SETTLEMENT_VERIFICATION_STATUS.pending },
    );
  }
  if (draftFilters.settlement_code_verified) {
    return withSettlementVerification(
      {
        ...urlFilters,
        address_object_name: draftFilters.address_object_name_verified
          && String(draftFilters.address_object || "") === String(urlFilters.address_object || "")
          ? (draftFilters.address_object_name || "")
          : urlFilters.address_object_name,
        address_object_name_verified: Boolean(
          draftFilters.address_object_name_verified
          && String(draftFilters.address_object || "") === String(urlFilters.address_object || ""),
        ),
      },
      { verified: true, status: SETTLEMENT_VERIFICATION_STATUS.verified },
    );
  }
  const draftStatus = draftFilters.settlement_verification_status;
  if (
    draftStatus === SETTLEMENT_VERIFICATION_STATUS.rejected
    || draftStatus === SETTLEMENT_VERIFICATION_STATUS.error
  ) {
    return withSettlementVerification(
      { ...urlFilters, settlement_code: draftCode },
      {
        verified: false,
        status: draftStatus,
        error: draftFilters.settlement_verification_error || "",
      },
    );
  }
  return withSettlementVerification(
    { ...urlFilters },
    { verified: false, status: SETTLEMENT_VERIFICATION_STATUS.pending },
  );
}

export function mergeUrlHydratedFilters(urlFilters, draftFilters) {
  return mergeSettlementVerification(urlFilters, draftFilters);
}

/** Defer Property fetch while a URL settlement candidate awaits unit lookup. */
export function shouldDeferPropertyFetchUntilSettlementVerified(filters) {
  const status = filters.settlement_verification_status;
  if (
    status === SETTLEMENT_VERIFICATION_STATUS.rejected
    || status === SETTLEMENT_VERIFICATION_STATUS.error
  ) {
    return false;
  }
  const region = (filters.region_code || "").trim();
  const settlement = (filters.settlement_code || "").trim();
  return Boolean(region && settlement && !filters.settlement_code_verified);
}

export function shouldSkipPropertyFetchForSettlement(filters) {
  const status = filters.settlement_verification_status;
  return (
    status === SETTLEMENT_VERIFICATION_STATUS.rejected
    || status === SETTLEMENT_VERIFICATION_STATUS.error
  );
}

export function settlementVerificationErrorMessage(filters) {
  return (
    (filters && filters.settlement_verification_error)
    || "Не вдалося перевірити населений пункт."
  );
}

function appendCommonFiltersToSearchParams(p, filters) {
  const geo = normalizeGeographyFilters(filters);
  if (filters.q) p.set("q", filters.q.trim());
  if (filters.deal_type) p.set("deal_type", filters.deal_type);
  if (filters.property_type) p.set("property_type", filters.property_type);
  if (geo.region_code) p.set("region_code", geo.region_code.trim());
  if (geo.settlement_code) p.set("settlement_code", geo.settlement_code.trim());
  if (geo.address_object) p.set("address_object", String(geo.address_object).trim());
  if (geo.local_area) p.set("local_area", geo.local_area.trim());
  if (filters.price_min) p.set("price_min", String(filters.price_min));
  if (filters.price_max) p.set("price_max", String(filters.price_max));
  if (filters.currency && filters.currency !== "USD") {
    p.set("currency", filters.currency);
  }
  if (filters.area_min) p.set("area_min", String(filters.area_min));
  if (filters.area_max) p.set("area_max", String(filters.area_max));
  if (filters.rooms_min) p.set("rooms_min", String(filters.rooms_min));
  if (filters.rooms_max) p.set("rooms_max", String(filters.rooms_max));
  if (filters.floor_min) p.set("floor_min", String(filters.floor_min));
  if (filters.floor_max) p.set("floor_max", String(filters.floor_max));
  if (filters.land_purpose) p.set("land_purpose", filters.land_purpose);
  if (filters.suitable_for) p.set("suitable_for", filters.suitable_for);
  if (filters.residential_complex) {
    p.set("residential_complex", filters.residential_complex.trim());
  }
  if (filters.cadastral_number) {
    p.set("cadastral_number", filters.cadastral_number.trim());
  }
  if (filters.walls) p.set("walls", filters.walls);
  if (filters.heating) p.set("heating", filters.heating);
  if (filters.condition) p.set("condition", filters.condition);
  if (filters.furniture) p.set("furniture", filters.furniture);
  for (const feature of filters.features || []) {
    p.append("features", feature);
  }
  if (filters.has_housing_program) p.set("has_housing_program", "true");
  if (filters.has_recovery_program) p.set("has_recovery_program", "true");
  const compensation = (filters.compensation || "").trim();
  if (compensation) {
    p.set("compensation", compensation);
  }
  const compensationCurrency = (filters.compensation_currency || "").trim();
  if (compensationCurrency) {
    p.set("compensation_currency", compensationCurrency);
  }
}

export function sanitizeRegionCodeInFilters(filters) {
  const code = (filters.region_code || "").trim();
  if (!code) {
    return filters.region_code ? { ...filters, region_code: "" } : filters;
  }
  if (!KATOTTG_CODE_RE.test(code)) {
    return { ...filters, region_code: "" };
  }
  return { ...filters, region_code: code };
}

export function readFiltersFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const get = (key) => params.get(key) || "";
  const getAll = (key) => params.getAll(key);
  const getBool = (key) => params.get(key) === "true";

  // Compensation values are trimmed but NOT stripped — invalid non-empty tokens
  // are preserved so screens can validate and surface errors to the user
  // rather than silently broadening the query.
  const geo = normalizeGeographyFilters({
      q: get("q"),
      deal_type: get("deal_type"),
      property_type: get("property_type"),
      region_code: get("region_code").trim(),
      settlement_code: get("settlement_code").trim(),
      address_object: get("address_object").trim(),
      local_area: get("local_area").trim(),
      price_min: get("price_min"),
      price_max: get("price_max"),
      currency: get("currency") || "USD",
      area_min: get("area_min"),
      area_max: get("area_max"),
      rooms_min: get("rooms_min"),
      rooms_max: get("rooms_max"),
      floor_min: get("floor_min"),
      floor_max: get("floor_max"),
      land_purpose: get("land_purpose"),
      suitable_for: get("suitable_for"),
      residential_complex: get("residential_complex"),
      cadastral_number: get("cadastral_number"),
      walls: get("walls"),
      heating: get("heating"),
      condition: get("condition"),
      furniture: get("furniture"),
      features: getAll("features"),
      has_housing_program: getBool("has_housing_program"),
      has_recovery_program: getBool("has_recovery_program"),
      compensation: (get("compensation") || "").trim(),
      compensation_currency: (get("compensation_currency") || "").trim(),
    });
  return {
    ...geo,
    address_object_name: "",
    address_object_name_verified: false,
    settlement_code_verified: false,
    settlement_verification_status: geo.settlement_code
      ? SETTLEMENT_VERIFICATION_STATUS.pending
      : SETTLEMENT_VERIFICATION_STATUS.idle,
    settlement_verification_error: "",
  };
}

export function buildFilterSearchString(filters, options = {}) {
  const p = new URLSearchParams();
  appendCommonFiltersToSearchParams(p, filters);
  const sort = options.sort;
  if (sort && sort !== DEFAULT_SORT) {
    p.set("sort", sort);
  }
  return p.toString();
}

export function buildPairedSurfaceHref(pathname, filters, options = {}) {
  const search = buildFilterSearchString(filters, options);
  return search ? `${pathname}?${search}` : pathname;
}

export function writeFiltersToUrl(navigate, pathname, filters, sort) {
  const p = new URLSearchParams();
  appendCommonFiltersToSearchParams(p, filters);
  if (sort && sort !== DEFAULT_SORT) {
    p.set("sort", sort);
  }
  navigate({ pathname, search: p.toString() }, { replace: false });
}

export function applyCanonicalRegionSelection(filters, regionCode) {
  const prevCode = (filters.region_code || "").trim();
  const nextCode = (regionCode || "").trim();
  const regionChanged = prevCode !== nextCode;

  return normalizeGeographyFilters({
    ...filters,
    region_code: nextCode,
    settlement_code: regionChanged ? "" : (filters.settlement_code || "").trim(),
    settlement_code_verified: false,
    settlement_verification_status: regionChanged
      ? SETTLEMENT_VERIFICATION_STATUS.idle
      : SETTLEMENT_VERIFICATION_STATUS.pending,
    settlement_verification_error: "",
    address_object: regionChanged ? "" : (filters.address_object || ""),
    address_object_name: regionChanged ? "" : (filters.address_object_name || ""),
    address_object_name_verified: regionChanged
      ? false
      : Boolean(filters.address_object_name_verified),
  });
}

export function applyCanonicalSettlementSelection(filters, settlementCode, options = {}) {
  const prevCode = (filters.settlement_code || "").trim();
  const code = (settlementCode || "").trim();
  const verified = code ? options.verified !== false : false;
  const settlementChanged = prevCode !== code;
  const status = !code
    ? SETTLEMENT_VERIFICATION_STATUS.idle
    : (verified
      ? SETTLEMENT_VERIFICATION_STATUS.verified
      : SETTLEMENT_VERIFICATION_STATUS.pending);
  return normalizeGeographyFilters({
    ...filters,
    settlement_code: code,
    settlement_code_verified: verified,
    settlement_verification_status: status,
    settlement_verification_error: "",
    address_object: settlementChanged ? "" : (filters.address_object || ""),
    address_object_name: settlementChanged ? "" : (filters.address_object_name || ""),
    address_object_name_verified: settlementChanged
      ? false
      : Boolean(filters.address_object_name_verified),
  });
}

export function applyCanonicalSettlementRejection(filters, options = {}) {
  const status = options.status === SETTLEMENT_VERIFICATION_STATUS.error
    ? SETTLEMENT_VERIFICATION_STATUS.error
    : SETTLEMENT_VERIFICATION_STATUS.rejected;
  const error = options.error
    || (status === SETTLEMENT_VERIFICATION_STATUS.error
      ? "Не вдалося перевірити населений пункт. Спробуйте ще раз."
      : "Некоректний або неактивний населений пункт.");
  return withSettlementVerification(
    {
      ...filters,
      settlement_code: (filters.settlement_code || "").trim(),
    },
    { verified: false, status, error },
  );
}

function appendGeographyBackendParams(out, filters) {
  const geo = normalizeGeographyFilters(filters);
  const regionCode = geo.region_code && geo.region_code.trim();
  if (regionCode) {
    out.region_code = regionCode;
    if (geo.settlement_code && filters.settlement_code_verified) {
      out.settlement_code = geo.settlement_code;
    }
  }
  const addressObject = String(geo.address_object || "").trim();
  if (addressObject) {
    out.address_object = addressObject;
  }
  const localArea = String(geo.local_area || "").trim();
  if (localArea) {
    out.local_area = localArea;
  }
}

export function buildBackendParams(filters) {
  const out = {};

  if (filters.q && filters.q.trim()) out.search = filters.q.trim();
  if (filters.deal_type) out.deal_type = filters.deal_type;
  if (filters.property_type) {
    out.property_type = filters.property_type;
  }

  appendGeographyBackendParams(out, filters);

  if (
    filters.price_min !== "" &&
    filters.price_min !== null &&
    filters.price_min !== undefined
  ) {
    out.min_price = filters.price_min;
  }
  if (
    filters.price_max !== "" &&
    filters.price_max !== null &&
    filters.price_max !== undefined
  ) {
    out.max_price = filters.price_max;
  }
  if (filters.currency && filters.currency !== "USD") {
    out.currency = filters.currency;
  }

  const areaLand = filters.property_type === "land";
  const areaMinKey = areaLand ? "min_land_area" : "min_total_area";
  const areaMaxKey = areaLand ? "max_land_area" : "max_total_area";
  if (
    filters.area_min !== "" &&
    filters.area_min !== null &&
    filters.area_min !== undefined
  ) {
    out[areaMinKey] = filters.area_min;
  }
  if (
    filters.area_max !== "" &&
    filters.area_max !== null &&
    filters.area_max !== undefined
  ) {
    out[areaMaxKey] = filters.area_max;
  }

  if (
    filters.rooms_min !== "" &&
    filters.rooms_min !== null &&
    filters.rooms_min !== undefined
  ) {
    out.rooms_min = filters.rooms_min;
  }
  if (
    filters.rooms_max !== "" &&
    filters.rooms_max !== null &&
    filters.rooms_max !== undefined
  ) {
    out.rooms_max = filters.rooms_max;
  }

  if (filters.has_housing_program) out.has_housing_program = true;
  if (filters.has_recovery_program) out.has_recovery_program = true;

  if (filters.floor_min) out.floor_min = filters.floor_min;
  if (filters.floor_max) out.floor_max = filters.floor_max;
  if (filters.land_purpose) out.land_purpose = filters.land_purpose;
  if (filters.suitable_for) out.suitable_for = filters.suitable_for;

  if (filters.residential_complex && filters.residential_complex.trim()) {
    out.residential_complex = filters.residential_complex.trim();
  }
  if (filters.cadastral_number && filters.cadastral_number.trim()) {
    out.cadastral_number = filters.cadastral_number.trim();
  }
  if (filters.walls) out.walls = filters.walls;
  if (filters.heating) out.heating = filters.heating;
  if (filters.condition) out.condition = filters.condition;
  if (filters.furniture) out.furniture = filters.furniture;

  // Pass raw non-empty values through: invalid tokens intentionally reach the
  // backend (returning 400) rather than being silently broadened to no-filter.
  // Screens gate the actual fetch via validatePropertyCompensationFilters.
  const compensation = (filters.compensation || "").trim();
  if (compensation) {
    out.compensation = compensation;
  }
  const compensationCurrency = (filters.compensation_currency || "").trim();
  if (compensationCurrency) {
    out.compensation_currency = compensationCurrency;
  }

  return out;
}
